import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Mail, CheckCircle, RefreshCw, Send } from 'lucide-react';
import { useAuth } from './AuthProvider';
import { useToast } from '@/hooks/use-toast';
import { useI18n } from '../i18n/i18n';
import { sendVerificationEmailToUser } from '../lib/firebase';

interface EmailVerificationGateProps {
  children: React.ReactNode;
}

export const EmailVerificationGate: React.FC<EmailVerificationGateProps> = ({ children }) => {
  const { user, isEmailVerified, refreshUser } = useAuth();
  const { toast } = useToast();
  const { t } = useI18n();
  const [isResending, setIsResending] = useState(false);
  const [lastResendTime, setLastResendTime] = useState<number | null>(null);
  const [countdown, setCountdown] = useState(0);

  // Countdown timer for resend button
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // Check if user is logged in and email is not verified
  if (!user || isEmailVerified) {
    return <>{children}</>;
  }

  const handleResendVerification = async () => {
    if (!user || isResending || countdown > 0) return;

    try {
      setIsResending(true);
      await sendVerificationEmailToUser(user);
      
      toast({
        title: t('auth.verification.emailSent'),
        description: t('auth.verification.checkInbox'),
        duration: 5000,
      });

      // Set countdown for 60 seconds
      setLastResendTime(Date.now());
      setCountdown(60);
    } catch (error: any) {
      console.error('Error sending verification email:', error);
      toast({
        title: t('auth.verification.error'),
        description: error.message || t('auth.verification.genericError'),
        variant: 'destructive',
        duration: 5000,
      });
    } finally {
      setIsResending(false);
    }
  };

  const handleCheckVerification = async () => {
    try {
      const isVerified = await refreshUser(user.email || undefined);
      if (isVerified) {
        toast({
          title: t('auth.verification.success'),
          description: t('auth.verification.welcomeMessage'),
          duration: 5000,
        });
      } else {
        toast({
          title: t('auth.verification.notVerifiedYet'),
          description: t('auth.verification.pleaseCheck'),
          variant: 'destructive',
          duration: 3000,
        });
      }
    } catch (error) {
      console.error('Error checking verification:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-lg">
        <CardContent className="p-8 text-center">
          <div className="mb-6">
            <Mail className="h-16 w-16 text-orange-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              {t('auth.verification.verifyEmailTitle')}
            </h1>
            <p className="text-gray-600">
              {t('auth.verification.verifyEmailMessage')}
            </p>
          </div>

          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-orange-800">
              <strong>{t('auth.verification.emailSentTo')}:</strong>
              <br />
              {user.email}
            </p>
          </div>

          <div className="space-y-3">
            <Button
              onClick={handleCheckVerification}
              className="w-full bg-[#EA580C] hover:bg-[#C2410C] text-white"
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              {t('auth.verification.checkVerification')}
            </Button>

            <Button
              onClick={handleResendVerification}
              disabled={isResending || countdown > 0}
              variant="outline"
              className="w-full"
            >
              {isResending ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              {countdown > 0 
                ? `${t('auth.verification.resendIn')} ${countdown}s`
                : isResending 
                  ? t('auth.verification.sending')
                  : t('auth.verification.resendEmail')
              }
            </Button>
          </div>

          <div className="mt-6 text-xs text-gray-500">
            <p>{t('auth.verification.checkSpam')}</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};